import BaseMenu from './BaseMenu';
import BaseUISystem from '../BaseUISystem';
import { AUDIO_UI_BUTTON_CLOSE, AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { applyDefaultButtonAnimation } from '../UIHelper';
import Game from '../../../Game';
import {
    GetAnimationClip,
    PlayAnimation,
} from '../../Animation/AnimationHelper';
import IPlayerData from '../../../Player/IPlayerData';
import UITextComponent from '../../../Component/UI/Text';
import { DataConflictActions } from '../../../State/Game/DataConflictActions';
import { AsyncActionStatus } from '../../../State/AsyncAction';
import { UIActions } from '../../../State/UI/UIActions';

const DATA_CONTAINER_OPTION_1 = 'main_container/first_selection_container';
const DATA_CONTAINER_OPTION_2 = 'main_container/second_selection_container';
const CONFIRM_DATA_CONTAINER = 'main_container/user_selected_container';
const CONFIRM_BTN_CONTAINER = 'main_container/button_selection_container';
const LEVEL_TEXT_1 =
    'main_container/first_selection_container/data_column/heading_text';
const LEVEL_TEXT_2 =
    'main_container/second_selection_container/data_column/heading_text';
const CONFIRM_LEVEL_TEXT =
    'main_container/user_selected_container/data_column/heading_text';
const STAR_TEXT_1 =
    'main_container/first_selection_container/data_column/stars_text';
const STAR_TEXT_2 =
    'main_container/second_selection_container/data_column/stars_text';
const CONFIRM_STAR_TEXT =
    'main_container/user_selected_container/data_column/stars_text';
const COIN_TEXT_1 =
    'main_container/first_selection_container/data_column/coins_text';
const COIN_TEXT_2 =
    'main_container/second_selection_container/data_column/coins_text';
const CONFIRM_COIN_TEXT =
    'main_container/user_selected_container/data_column/coins_text';
const DATA_SET_BTN_1 =
    'main_container/first_selection_container/data_column/submit_button';
const DATA_SET_BTN_2 =
    'main_container/second_selection_container/data_column/submit_button';
const CONFIRM_BTN =
    'main_container/button_selection_container/data_column/confirm_button';
const CANCEL_BTN =
    'main_container/button_selection_container/data_column/cancel_button';
const SELECT_TEXT_1 =
    'main_container/first_selection_container/data_column/submit_button/select_first_data_text';
const SELECT_TEXT_2 =
    'main_container/second_selection_container/data_column/submit_button/select_second_data_text';
const CONFIRM_TEXT =
    'main_container/button_selection_container/data_column/confirm_button/confirm_data_text';
const CANCEL_TEXT =
    'main_container/button_selection_container/data_column/cancel_button/cancel_data_text';

export default class ResolveDataConflictMenuHandler extends BaseMenu {
    private newPlayerData: IPlayerData;
    private isVisibleToggle: boolean = true;

    constructor(uiSystem: BaseUISystem) {
        super('resolveDataConflictMenu', uiSystem);
    }

    protected async onCreate() {
        this.setUpChooseData();
        this.setUpConfirmData();
        this.element.find(
            DATA_CONTAINER_OPTION_1
        ).container.visible = this.isVisibleToggle;
        this.element.find(
            DATA_CONTAINER_OPTION_2
        ).container.visible = this.isVisibleToggle;
        this.element.find(CONFIRM_DATA_CONTAINER).container.visible = !this
            .isVisibleToggle;
        this.element.find(CONFIRM_BTN_CONTAINER).container.visible = !this
            .isVisibleToggle;
    }

    public onStart(): void {
        const state = Game.state.getState();
        // Check if popup should be triggered (if UIState got cleared after a scene change)
        if (
            state.dataConflict.status === AsyncActionStatus.IN_PROGRESS &&
            state.UIStatePrio.popupIDPrio !== 'resolveDataConflictMenu'
        ) {
            Game.state.dispatch(
                UIActions.showPrioPopup('resolveDataConflictMenu')
            );
        }
    }

    protected playHideAnimation() {
        return PlayAnimation(
            GetAnimationClip(this.clipHide, this.element, {
                duration: 500,
                startAlpha: 1,
                endAlpha: 0,
            })
        );
    }

    private setUpChooseData() {
        // data set up
        const dataConflicts = Game.state.getState().dataConflict.result;
        const playerData1: IPlayerData = dataConflicts[0];
        const playerData2: IPlayerData = dataConflicts[1];
        // text set up
        const levelText1 = this.element.find(LEVEL_TEXT_1);
        const levelText2 = this.element.find(LEVEL_TEXT_2);
        const starText1 = this.element.find(STAR_TEXT_1);
        const starText2 = this.element.find(STAR_TEXT_2);
        const coinTxt1 = this.element.find(COIN_TEXT_1);
        const coinTxt2 = this.element.find(COIN_TEXT_2);
        const levelTextComponent1 = levelText1.getComponent(UITextComponent);
        const levelTextComponent2 = levelText2.getComponent(UITextComponent);
        const starTextComponent1 = starText1.getComponent(UITextComponent);
        const starTextComponent2 = starText2.getComponent(UITextComponent);
        const coinTextComponent1 = coinTxt1.getComponent(UITextComponent);
        const coinTextComponent2 = coinTxt2.getComponent(UITextComponent);
        const dataLevels1: number[] = JSON.parse(playerData1.level_star);
        const dataLevels2: number[] = JSON.parse(playerData2.level_star);

        const selectTxt1 = this.element
            .find(SELECT_TEXT_1)
            .getComponent(UITextComponent);
        const selectTxt2 = this.element
            .find(SELECT_TEXT_2)
            .getComponent(UITextComponent);
        const cancelTxt = this.element
            .find(CANCEL_TEXT)
            .getComponent(UITextComponent);
        const confirmTxt = this.element
            .find(CONFIRM_TEXT)
            .getComponent(UITextComponent);
        selectTxt1.text = selectTxt1.text.toUpperCase();
        selectTxt2.text = selectTxt2.text.toUpperCase();
        cancelTxt.text = cancelTxt.text.toUpperCase();
        confirmTxt.text = confirmTxt.text.toUpperCase();

        levelTextComponent1.text = Game.locale
            .getText('Level')
            .replace('[x]', `${dataLevels1.length}`)
            .toUpperCase();

        levelTextComponent2.text = Game.locale
            .getText('Level')
            .replace('[x]', `${dataLevels2.length}`)
            .toUpperCase();

        const starsList1: number[] = JSON.parse(playerData1.level_star);
        starTextComponent1.text = `${starsList1.reduce((i, d) => i + d, 0)}`;
        const starsList2: number[] = JSON.parse(playerData2.level_star);
        starTextComponent2.text = `${starsList2.reduce((i, d) => i + d, 0)}`;
        coinTextComponent1.text = `${playerData1.currencies}`;
        coinTextComponent2.text = `${playerData2.currencies}`;

        // button set up
        const selectBtn1 = this.element.find(DATA_SET_BTN_1);
        selectBtn1.on(GameObjectEvent.SELECTED, async () => {
            this.setConfirmData(playerData1);
            this.toggleScene();
        });
        applyDefaultButtonAnimation(selectBtn1, AUDIO_UI_BUTTON_DEFAULT);

        const selectBtn2 = this.element.find(DATA_SET_BTN_2);
        selectBtn2.on(GameObjectEvent.SELECTED, async () => {
            this.setConfirmData(playerData2);
            this.toggleScene();
        });
        applyDefaultButtonAnimation(selectBtn2, AUDIO_UI_BUTTON_DEFAULT);
    }

    private setUpConfirmData() {
        const btnSubmit = this.element.find(CONFIRM_BTN);
        btnSubmit.on(GameObjectEvent.SELECTED, async () => {
            if (!this.isNewDataStateSet()) {
                return;
            }

            Game.state.dispatch(
                DataConflictActions.resolveConflicts(this.newPlayerData)
            );
        });
        applyDefaultButtonAnimation(btnSubmit, AUDIO_UI_BUTTON_DEFAULT);

        const btnCancel = this.element.find(CANCEL_BTN);
        btnCancel.on(GameObjectEvent.SELECTED, async () => {
            this.toggleScene();
        });
        applyDefaultButtonAnimation(btnCancel, AUDIO_UI_BUTTON_CLOSE);
    }

    private toggleScene() {
        // TODO: Add animation to make this more visually appealing then just show and hide.
        this.isVisibleToggle = !this.isVisibleToggle;
        const isScene1Visible = this.isVisibleToggle;
        const isScene2Visible = !this.isVisibleToggle;
        this.element.find(
            DATA_CONTAINER_OPTION_1
        ).container.visible = isScene1Visible;
        this.element.find(
            DATA_CONTAINER_OPTION_2
        ).container.visible = isScene1Visible;

        this.element.find(
            CONFIRM_DATA_CONTAINER
        ).container.visible = isScene2Visible;
        this.element.find(
            CONFIRM_BTN_CONTAINER
        ).container.visible = isScene2Visible;
    }

    private isNewDataStateSet() {
        return this.newPlayerData !== undefined;
    }

    private setConfirmData(chosenPlayerData: IPlayerData) {
        this.newPlayerData = chosenPlayerData;

        const levelText = this.element.find(CONFIRM_LEVEL_TEXT);
        const starText = this.element.find(CONFIRM_STAR_TEXT);
        const coinText = this.element.find(CONFIRM_COIN_TEXT);
        const levelTextComponent = levelText.getComponent(UITextComponent);
        const starTextComponent = starText.getComponent(UITextComponent);
        const coinTextComponent = coinText.getComponent(UITextComponent);
        const newPlayerLevelProgression: number[] = JSON.parse(
            this.newPlayerData.level_star
        );
        levelTextComponent.text = Game.locale
            .getText('Level')
            .replace('[x]', `${newPlayerLevelProgression.length}`)
            .toUpperCase();
        const stars: number[] = JSON.parse(this.newPlayerData.level_star);
        starTextComponent.text = `${stars.reduce((i, d) => i + d, 0)}`;
        coinTextComponent.text = `${this.newPlayerData.currencies}`;
    }
}
